import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { getData, getFile } from "features/dinamic";
import { setDataToast, setVisibleToast } from "features/toast";
import { setLoading } from "features/loader";
import AlertInterface from "features/toast/toastInterface";
import saveAs from "file-saver";

interface AlumnoSesionState {
  status: string;
  error: string;
  sesiones: any[];
  sesionDetail: any | null;
  recursos: any[];
  actividades: any[];
}

const initialState: AlumnoSesionState = {
  status: 'idle',
  error: '',
  sesiones: [],
  sesionDetail: null,
  recursos: [],
  actividades: [],
}

export const fetchAlumnoSesiones = createAsyncThunk('alumno/fetchSesiones', async ({ idModulo, idGrupo }: any, { dispatch, getState, rejectWithValue }) => {
  const idsp = 204;
  const params: any = { idsp, idModulo };
  if (idGrupo) {
    params.idGrupo = idGrupo;
  }
  const { result, message, data } = await getData(params, getState, dispatch);
  if (result) {
    return { data };
  }
  return rejectWithValue(message);
});

export const fetchAlumnoSesionDetail = createAsyncThunk('alumno/fetchSesionDetail', async ({ idGrupoSesion }: any, { dispatch, getState, rejectWithValue }) => {
  const idsp = 205;
  const { result, message, data } = await getData({ idsp, idGrupoSesion }, getState, dispatch);
  if (result) {
    return { data };
  }
  return rejectWithValue(message);
});

export const fetchAlumnoSesionRecursos = createAsyncThunk('alumno/fetchSesionRecursos', async ({ idGrupoSesion }: any, { dispatch, getState, rejectWithValue }) => {
  const idsp = 206;
  const { result, message, data } = await getData({ idsp, idGrupoSesion }, getState, dispatch);
  if (result) {
    return { data };
  }
  return rejectWithValue(message);
});

export const fetchAlumnoSesionActividades = createAsyncThunk('alumno/fetchSesionActividades', async ({ idGrupoSesion }: any, { dispatch, getState, rejectWithValue }) => {
  const idsp = 207;
  const { result, message, data } = await getData({ idsp, idGrupoSesion }, getState, dispatch);
  if (result) {
    return { data };
  }
  return rejectWithValue(message);
});

export const downloadGuiaAlumno = createAsyncThunk('alumno/downloadGuia', async (_: any, { dispatch, getState, rejectWithValue }) => {
  const state: any = getState();
  const sesionDetail = selectAlumnoSesionDetail(state);
  let alert: AlertInterface;

  if (sesionDetail?.fileUrlGuia) {
    const fileExtension = sesionDetail.fileUrlGuia.substring(sesionDetail.fileUrlGuia.lastIndexOf('.'));
    const fileName = `guia_${sesionDetail.nombreSesion}${fileExtension}`;
    const response = await getFile({ fileUrl: sesionDetail.fileUrlGuia, fileName: fileName });

    if (response && response.blob) {
      saveAs(response.blob, fileName);
      alert = { title: 'CORRECTO..!', message: "GUÍA DESCARGADA CORRECTAMENTE", data: [], type: 'LimeGreen', time: Date.now() };
    } else {
      alert = { title: 'ACCIÓN INCOMPLETA', message: "GUÍA NO DISPONIBLE", data: [], type: 'Crimson', time: Date.now() };
    }
  } else {
    alert = { title: 'ACCIÓN INCOMPLETA', message: "GUÍA NO DISPONIBLE", data: [], type: 'Crimson', time: Date.now() };
  }
  dispatch(setDataToast(alert));
  dispatch(setVisibleToast(true));
  dispatch(setLoading(false));
  setTimeout(() => {
    dispatch(setVisibleToast(false));
  }, 4000);
});

export const downloadRecursoSesion = createAsyncThunk('alumno/downloadRecurso', async ({ fileUrl, fileName }: any, { dispatch, rejectWithValue }) => {
  let alert: AlertInterface;

  if (fileUrl) {
    const fileExtension = fileUrl.substring(fileUrl.lastIndexOf('.'));
    const fullFileName = `${fileName}${fileExtension}`;
    const response = await getFile({ fileUrl: fileUrl, fileName: fullFileName });

    if (response && response.blob) {
      saveAs(response.blob, fullFileName);
      alert = { title: 'CORRECTO..!', message: "ARCHIVO DESCARGADO CORRECTAMENTE", data: [], type: 'LimeGreen', time: Date.now() };
    } else {
      alert = { title: 'ACCIÓN INCOMPLETA', message: "ARCHIVO NO DISPONIBLE", data: [], type: 'Crimson', time: Date.now() };
    }
  } else {
    alert = { title: 'ACCIÓN INCOMPLETA', message: "ARCHIVO NO DISPONIBLE", data: [], type: 'Crimson', time: Date.now() };
  }

  dispatch(setDataToast(alert));
  dispatch(setVisibleToast(true));
  dispatch(setLoading(false));
  setTimeout(() => {
    dispatch(setVisibleToast(false));
  }, 4000);
});

const alumnoSesionSlice = createSlice({
  name: 'alumnoSesion',
  initialState,
  reducers: {
    setSesionDetail: (state, action) => {
      state.sesionDetail = action.payload;
    },
    clearSesiones: (state) => {
      state.sesiones = [];
      state.sesionDetail = null;
      state.recursos = [];
      state.actividades = [];
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchAlumnoSesiones.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAlumnoSesiones.fulfilled, (state, action) => {
        state.status = 'completed';
        state.sesiones = Array.isArray(action.payload.data) ? action.payload.data : [];
      })
      .addCase(fetchAlumnoSesiones.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      .addCase(fetchAlumnoSesionDetail.fulfilled, (state, action) => {
        const data = action.payload?.data;
        state.sesionDetail = Array.isArray(data) ? data[0] : data;
      })
      .addCase(fetchAlumnoSesionRecursos.fulfilled, (state, action) => {
        state.recursos = Array.isArray(action.payload.data) ? action.payload.data : [];
      })
      .addCase(fetchAlumnoSesionActividades.fulfilled, (state, action) => {
        state.actividades = Array.isArray(action.payload.data) ? action.payload.data : [];
      })
  }
});

export const selectAlumnoSesiones = (state: RootState) => state.alumnoSesion.sesiones;
export const selectAlumnoSesionDetail = (state: RootState) => state.alumnoSesion.sesionDetail;
export const selectAlumnoSesionRecursos = (state: RootState) => state.alumnoSesion.recursos;
export const selectAlumnoSesionActividades = (state: RootState) => state.alumnoSesion.actividades;
export const selectAlumnoSesionStatus = (state: RootState) => state.alumnoSesion.status;

export const { setSesionDetail, clearSesiones } = alumnoSesionSlice.actions;

export default alumnoSesionSlice.reducer;